"use client";

import { useState } from "react";
import { useLocation } from "react-router-dom";
import { Tabs } from "@/shared/components/ui/tabs";

import type { VoiceCallLog } from "@/modules/hrPortal/types";

import useHRDashboard from "@/modules/hrPortal/hooks/useHRDashboard";

import DashboardTab from "./HRDashboard/DashboardTab";
import CandidatesTab from "./HRDashboard/CandidatesTab";
import VoiceCallsTab from "./HRDashboard/VoiceCallsTab";
import JobsTab from "./HRDashboard/JobsTab.tsx";
import ResumeDatabaseTab from "./HRDashboard/ResumeDatabaseTab";
import VoiceCallDetailsDialog from "./HRDashboard/VoiceCallDetailsDialog";
import ScheduleVoiceCallDialog from "./HRDashboard/ScheduleVoiceCallDialog";

export default function HRDashboard() {
  const {
    user,
    candidates,
    pagination,
    candidatesLoading,
    candidatesError,
    dashboardStats,
    voiceCallLogs,
    handleCandidatePageChange,
    handleCandidateFiltersChange,
  } = useHRDashboard();

  const [
    selectedCallLog,
    setSelectedCallLog,
  ] = useState<VoiceCallLog | null>(null);

  const [
    showCallDialog,
    setShowCallDialog,
  ] = useState(false);

  const [
    showScheduleDialog,
    setShowScheduleDialog,
  ] = useState(false);

  const [
    scheduleData,
    setScheduleData,
  ] = useState({
    candidates: "",
    callTime: "",
    questions: "standard",
  });

  const location = useLocation();

  const getActiveTab = () => {
    if (location.pathname === "/hr") {
      return "dashboard";
    }

    if (location.pathname.startsWith("/hr/candidates")) {
      return "candidates";
    }

    if (location.pathname.startsWith("/hr/voice-calls")) {
      return "voice-calls";
    }

    if (location.pathname.startsWith("/hr/jobs")) {
      return "jobs";
    }

    if (location.pathname.startsWith("/hr/resume-database")) {
      return "database";
    }

    return "dashboard";
  };

  const isHRAdmin =
    user?.role?.trim().toUpperCase() === "HR";

  const activeTab = getActiveTab();

  const handleCallSelect = (
    call: VoiceCallLog
  ) => {
    setSelectedCallLog(call);
    setShowCallDialog(true);
  };

  const handleScheduleVoiceCalls = () => {
    setShowScheduleDialog(true);
  };

  return (
    <div className="min-h-screen bg-slate-50/50">
      <div className="mx-auto w-full max-w-7xl px-3 py-4 sm:px-5 md:py-6 lg:px-8">
        <Tabs
          value={activeTab}
          className="space-y-6"
        >
          <DashboardTab
            dashboardStats={dashboardStats}
            voiceCallLogs={voiceCallLogs}
            onCallSelect={handleCallSelect}
          />


          <CandidatesTab
            candidates={candidates}
            pagination={pagination}
            loading={candidatesLoading}
            error={candidatesError}
            onScheduleVoiceCalls={handleScheduleVoiceCalls}
            onPageChange={handleCandidatePageChange}
            onFiltersChange={handleCandidateFiltersChange}
            isHRAdmin={isHRAdmin}
          />


          <VoiceCallsTab
            dashboardStats={dashboardStats}
            voiceCallLogs={voiceCallLogs}
            onStartCampaign={
              handleScheduleVoiceCalls
            }
            onCallSelect={handleCallSelect}
          />

          <JobsTab />

          <ResumeDatabaseTab />
        </Tabs>
      </div>

      <VoiceCallDetailsDialog
        open={showCallDialog}
        onOpenChange={setShowCallDialog}
        selectedCallLog={selectedCallLog}
      />

      <ScheduleVoiceCallDialog
        open={showScheduleDialog}
        onOpenChange={setShowScheduleDialog}
        dashboardStats={dashboardStats}
        scheduleData={scheduleData}
        setScheduleData={setScheduleData}
      />
    </div>
  );
}